import { useCallback, useMemo } from 'react'
import {
  CallThinIcon,
  ChatOutlinedIcon,
  CopyIcon,
  PhoneDialOutlinedIcon,
} from '@weblite/icons'
import { styled } from '@mui/material/styles'
import { Divider as MuiDivider, Skeleton } from '@mui/material'
import { useConditionalHook } from '@weblite/use-conditional-hook'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { UserAvatar } from '$common/avatar'
import { AvatarSize } from '$common/avatar/avatar-base/types'
import { showPhoneNumberSavedInMemorySnackbar } from '$comp/snackbars'
import {
  ContextMenuItem,
  ContextMenuList,
} from '$comp/_app/components/chat/components/body/components/context-menu/components'
import { callService } from '$logic/call'
import { getPvByUserId } from '$logic/pv'
import { openRoom } from '$logic/opened-room'
import { openUserProfile, switchToMyAccount } from '$logic/router'
import { getPrivateSpace } from '$logic/private-space'
import { getUserDisplayName, useUserByPhone } from '$logic/users'
import { toEnglishPhoneNumber } from '$func/text'
import { copyToClipboard } from '$func/clipboard'
import { t } from '$i18n'

const SkeletonsContainer = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
`

const PhoneMenuSkeletonItem = styled(Skeleton)`
  height: 20px;
  margin: 12px;
`

interface Props {
  phone: string
  isOpen: boolean
  clientX: number
  clientY: number
  showAppOptions?: boolean
}

export function PhoneMenu({
  phone,
  isOpen,
  clientX,
  clientY,
  showAppOptions = true,
}: Props) {
  const englishPhone = useMemo(() => {
    return toEnglishPhoneNumber(phone)
  }, [phone])

  const { user, isClient, deleted } =
    useConditionalHook(showAppOptions, {
      hook: useUserByPhone,
      hookArgs: englishPhone,
      defaultValue: undefined,
    }) || {}

  const showMenuItemsSkeleton = showAppOptions && user === undefined && !deleted
  const userIsAvailable = Boolean(user && !deleted)

  const handleCopy = useCallback(async () => {
    copyToClipboard(phone)
    await closeCurrentModal()
    showPhoneNumberSavedInMemorySnackbar()
  }, [phone])

  const handleCallThroughPhone = useCallback(async () => {
    await closeCurrentModal()
    window.open(`tel:${englishPhone}`)
  }, [englishPhone])

  const handleCallThroughApp = useCallback(async () => {
    if (!user) return

    const pv = await getPvByUserId(user.id)
    await closeCurrentModal()

    if (pv?.roomId) callService.startCall(pv.roomId, 'PV', 'call')
  }, [user])

  const handleSendMessage = useCallback(async () => {
    if (!user) return
    await closeCurrentModal()
    if (isClient) openRoom(getPrivateSpace().roomId)
    else {
      const room = await getPvByUserId(user.id)
      if (room) openRoom(room.roomId)
    }
  }, [user, isClient])

  const handleOpenProfile = useCallback(async () => {
    if (!user) return
    await closeCurrentModal()
    if (isClient) switchToMyAccount()
    else openUserProfile(user.id)
  }, [user, isClient])

  return (
    <ContextMenuList
      open={isOpen}
      xAxis={clientX}
      yAxis={clientY}
      onClose={closeCurrentModal}
      loadingItems={showMenuItemsSkeleton}
    >
      {showMenuItemsSkeleton ? (
        <SkeletonsContainer>
          <PhoneMenuSkeletonItem variant="rounded" width={130} />
          <PhoneMenuSkeletonItem variant="rounded" width={60} />
        </SkeletonsContainer>
      ) : (
        <>
          {userIsAvailable && (
            <ContextMenuItem
              text={t('menu.phone.sendMessage')}
              onClick={handleSendMessage}
            >
              <ChatOutlinedIcon />
            </ContextMenuItem>
          )}

          {userIsAvailable && !isClient ? (
            <ContextMenuItem
              text={t('menu.phone.callThroughApp')}
              onClick={handleCallThroughApp}
            >
              <CallThinIcon />
            </ContextMenuItem>
          ) : undefined}

          <ContextMenuItem
            text={
              user
                ? t('menu.phone.callThroughPhone')
                : t('menu.phone.callThroughNetwork')
            }
            onClick={handleCallThroughPhone}
          >
            <PhoneDialOutlinedIcon />
          </ContextMenuItem>

          <ContextMenuItem text={t('menu.phone.copy')} onClick={handleCopy}>
            <CopyIcon />
          </ContextMenuItem>

          {userIsAvailable && <Divider />}
          {userIsAvailable && (
            <ContextMenuItem
              text={getUserDisplayName(user)}
              onClick={handleOpenProfile}
            >
              <UserAvatar userId={user!.id} size={AvatarSize._2XS} />
            </ContextMenuItem>
          )}
        </>
      )}
    </ContextMenuList>
  )
}

const Divider = styled(MuiDivider)(({ theme }) => ({
  borderColor: theme.palette.neutral200,
  margin: '0 !important',
  '.MuiDivider-wrapper': {
    display: 'none',
  },
  ':before': {
    borderColor: theme.palette.neutral200,
  },
  ':after': {
    borderColor: theme.palette.neutral200,
  },
}))
